import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Price from 'components/shared/Price'
import Button from 'components/shared/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import {
    DEFAULT_QUANTITY,
    removeRoute,
    setEditRoute,
} from 'store/transport-slice'
import { useTransport } from 'store/transport-slice/hooks'
import { useAutoScroll } from 'utils/hooks'
import { setIsErrorModalOpened } from 'store/general-slice'

interface BusSummaryProps {
    routeIndex: number
    isEdited: boolean
    isCtaDisabled: boolean
    setBookingConflict: (isConflict: boolean) => void
    hasAltEditMode?: boolean
    originTotalPrice: number
}

export const BusSummary = ({
    routeIndex,
    isEdited,
    isCtaDisabled,
    setBookingConflict,
    hasAltEditMode = false,
    originTotalPrice,
}: BusSummaryProps) => {
    const { isTablet } = useResolutions()
    const { calculateScroll } = useAutoScroll()
    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()
    const { addRouteToCart, editRouteInCart, isTransportProcessing } =
        useTransport(routeIndex)
    const {
        ticket: { quantity },
        unitPrice,
        isCompleted,
    } = useAppSelector((state) => state.transport.routes[routeIndex])

    const totalPrice = (quantity || DEFAULT_QUANTITY) * (unitPrice || 0)

    const handleAddRoute = () => {
        const action = isEdited ? editRouteInCart : addRouteToCart

        action().then((response) => {
            const { isSuccess, messages } = response

            if (isSuccess) {
                calculateScroll({
                    trigerringSection: 'transport',
                    trigerringAction: 'add',
                    index: routeIndex,
                })
                setBookingConflict(false)
            } else {
                if (
                    messages &&
                    messages.some((msg) => msg.property_path === 'time')
                )
                    setBookingConflict(true)
                else dispatch(setIsErrorModalOpened(true))
            }
        })
    }

    const handleCancelRoute = () => {
        if (isEdited) {
            dispatch(setEditRoute({ index: routeIndex, editState: 'declined' }))
            calculateScroll({
                trigerringSection: 'transport',
                trigerringAction: 'cancel_edit',
                index: routeIndex,
            })
        } else {
            dispatch(removeRoute({ index: routeIndex }))
            calculateScroll({
                trigerringSection: 'transport',
                trigerringAction: 'cancel_create',
            })
        }
    }

    return (
        <Box
            direction={['column', 'row']}
            isFullWidth
            align={['stretch', 'center']}
            justify={['flex-start', 'space-between']}
            mt={[24, 40]}
            gap={[12, 16]}
        >
            {isCompleted && !hasAltEditMode && (
                <Box
                    direction="row"
                    align="flex-start"
                    justify={['flex-end', 'flex-start']}
                >
                    <Text
                        variant="bodySmall"
                        color="secondary_500"
                        mr={4}
                        mt={[8, 12]}
                    >
                        <FormattedMessage
                            id="upsells.transport.summary_total"
                            defaultMessage="Razem:"
                        />
                    </Text>
                    <Price value={totalPrice} size="heading2" />
                </Box>
            )}
            {hasAltEditMode && (
                <Box
                    direction={['column', 'row']}
                    align={['flex-end', 'flex-start']}
                    gap={[0, 24]}
                    isFullWidth
                >
                    <Box
                        direction={['row', 'column']}
                        align={['center', 'flex-start']}
                        justify={['flex-end', 'flex-start']}
                    >
                        <Text variant="bodySmall" mr={4} color="grayscale_700">
                            {formatMessage({
                                id: 'selfcare.editor.current_price',
                                defaultMessage: 'Obecna cena:',
                            })}
                        </Text>
                        <Price
                            value={originTotalPrice}
                            size="heading4"
                            color="grayscale_700"
                        />
                    </Box>
                    <Box
                        direction={['row', 'column']}
                        align={['center', 'flex-start']}
                        justify={['flex-end', 'flex-start']}
                    >
                        <Text variant="bodySmall" mr={4} color="secondary_500">
                            {formatMessage({
                                id: 'selfcare.editor.new_price',
                                defaultMessage: 'Nowa cena:',
                            })}
                        </Text>

                        <Price
                            size="heading4"
                            value={
                                !isCtaDisabled && isCompleted
                                    ? totalPrice
                                    : null
                            }
                        />
                    </Box>
                </Box>
            )}
            <Box isFullWidth direction="row" align="center" justify="flex-end">
                <Text
                    as="button"
                    variant="bodyStrong"
                    color="grayscale_700"
                    onClick={() => {
                        !isTransportProcessing && handleCancelRoute()
                        setBookingConflict(false)
                    }}
                    mr={24}
                    data-testid={`transport-${routeIndex}-button-cancel`}
                >
                    <FormattedMessage
                        id="global.btn.cancel"
                        defaultMessage="Anuluj"
                    />
                </Text>
                <Button
                    variant="secondary"
                    width="initial"
                    hasArrow={false}
                    onClick={handleAddRoute}
                    isDisabled={
                        !isCompleted || isTransportProcessing || isCtaDisabled
                    }
                    isLoading={isTransportProcessing}
                    testId={`transport-${routeIndex}-button-${
                        isEdited ? 'save' : 'add'
                    }`}
                >
                    {isEdited && (
                        <FormattedMessage
                            id="global.btn.save_changes"
                            defaultMessage="Zapisz zmiany"
                        />
                    )}

                    {!isEdited &&
                        (isTablet ? (
                            <FormattedMessage
                                id="global.btn.add"
                                defaultMessage="Dodaj"
                            />
                        ) : (
                            <FormattedMessage
                                id="global.btn.add_to_order"
                                defaultMessage="Dodaj do zamówienia"
                            />
                        ))}
                </Button>
            </Box>
        </Box>
    )
}
